interface EnvVars {
  API_BASE_URL: string;
  API_TIMEOUT: number;
  NODE_ENV: string;
  APP_NAME: string;
  APP_VERSION: string;
  ENABLE_ANALYTICS: boolean;
  ENABLE_ERROR_REPORTING: boolean;
  ENABLE_DEBUG_MODE: boolean;
  ENABLE_DEV_PAYMENT_BYPASS: boolean;
  STRIPE_PUBLIC_KEY: string | undefined;
  BUNNY_CDN_URL: string | undefined;
  LAZY_LOAD_DELAY: number;
  DEBOUNCE_DELAY: number;
}

interface AppConfig {
  pagination: { defaultPageSize: number; pageSizeOptions: number[] };
  uploads: {
    maxFileSize: number;
    maxVideoSize: number;
    allowedImageTypes: string[];
    allowedVideoTypes: string[];
  };
  validation: {
    minPasswordLength: number;
    maxPasswordLength: number;
    minUsernameLength: number;
    maxUsernameLength: number;
    maxDescriptionLength: number;
  };
  ui: {
    toastDuration: number;
    modalTransitionDuration: number;
    skeletonLoadingLines: number;
  };
  cache: { userDataTTL: number; coursesDataTTL: number };
  retry: { maxAttempts: number; initialDelay: number; maxDelay: number };
}

export interface Environment {
  NODE_ENV: string;
  IS_DEVELOPMENT: boolean;
  IS_PRODUCTION: boolean;
  IS_TEST: boolean;
  API_BASE_URL: string;
  API_TIMEOUT: number;
  APP_NAME: string;
  APP_VERSION: string;
  FEATURES: {
    ANALYTICS: boolean;
    ERROR_REPORTING: boolean;
    DEBUG: boolean;
    ALLOW_PAYMENT_BYPASS: boolean;
  };
  STRIPE_PUBLIC_KEY: string | undefined;
  BUNNY_CDN_URL: string | undefined;
  LAZY_LOAD_DELAY: number;
  DEBOUNCE_DELAY: number;
  CONFIG: AppConfig;
}

const ENV_VARS: EnvVars = {
  API_BASE_URL:
    import.meta.env.VITE_API_BASE_URL || "http://localhost:8080/api",
  API_TIMEOUT: parseInt(import.meta.env.VITE_API_TIMEOUT) || 30000,
  NODE_ENV: import.meta.env.MODE || "development",
  APP_NAME: import.meta.env.VITE_APP_NAME || "MusiCloud",
  APP_VERSION: import.meta.env.VITE_APP_VERSION || "1.0.0",
  ENABLE_ANALYTICS: import.meta.env.VITE_ENABLE_ANALYTICS === "true",
  ENABLE_ERROR_REPORTING:
    import.meta.env.VITE_ENABLE_ERROR_REPORTING === "true",
  ENABLE_DEBUG_MODE: import.meta.env.VITE_DEBUG_MODE === "true",
  ENABLE_DEV_PAYMENT_BYPASS:
    import.meta.env.VITE_ENABLE_DEV_PAYMENT_BYPASS === "true",
  STRIPE_PUBLIC_KEY: import.meta.env.VITE_STRIPE_PUBLIC_KEY,
  BUNNY_CDN_URL: import.meta.env.VITE_BUNNY_CDN_URL,
  LAZY_LOAD_DELAY: parseInt(import.meta.env.VITE_LAZY_LOAD_DELAY) || 300,
  DEBOUNCE_DELAY: parseInt(import.meta.env.VITE_DEBOUNCE_DELAY) || 500,
};

const IS_DEVELOPMENT = ENV_VARS.NODE_ENV === "development";
const IS_PRODUCTION = ENV_VARS.NODE_ENV === "production";
const IS_TEST = ENV_VARS.NODE_ENV === "test";

const APP_CONFIG: AppConfig = {
  pagination: {
    defaultPageSize: 12,
    pageSizeOptions: [6, 12, 24, 48],
  },
  uploads: {
    maxFileSize: 100 * 1024 * 1024,
    maxVideoSize: 500 * 1024 * 1024,
    allowedImageTypes: ["image/jpeg", "image/png", "image/gif", "image/webp"],
    allowedVideoTypes: ["video/mp4", "video/webm", "video/ogg"],
  },
  validation: {
    minPasswordLength: 8,
    maxPasswordLength: 128,
    minUsernameLength: 3,
    maxUsernameLength: 30,
    maxDescriptionLength: 2000,
  },
  ui: {
    toastDuration: 5000,
    modalTransitionDuration: 300,
    skeletonLoadingLines: 5,
  },
  cache: {
    userDataTTL: 5 * 60 * 1000,
    coursesDataTTL: 10 * 60 * 1000,
  },
  retry: {
    maxAttempts: 3,
    initialDelay: 1000,
    maxDelay: 5000,
  },
};

const validateConfig = (): boolean => {
  const errors: string[] = [];

  if (!ENV_VARS.API_BASE_URL) {
    errors.push("API_BASE_URL is required");
  }

  if (IS_PRODUCTION) {
    if (!ENV_VARS.STRIPE_PUBLIC_KEY) {
      console.warn("Warning: STRIPE_PUBLIC_KEY is not set in production");
    }
  }

  if (errors.length > 0) {
    throw new Error(
      `Environment configuration errors:\n${errors.join("\n")}`
    );
  }

  return true;
};

if (IS_DEVELOPMENT) {
  try {
    validateConfig();
    console.log("✅ Environment configuration validated");
  } catch (error) {
    console.error(
      "❌ Environment configuration error:",
      (error as Error).message
    );
  }
}

export const ENV: Environment = {
  NODE_ENV: ENV_VARS.NODE_ENV,
  IS_DEVELOPMENT,
  IS_PRODUCTION,
  IS_TEST,
  API_BASE_URL: ENV_VARS.API_BASE_URL,
  API_TIMEOUT: ENV_VARS.API_TIMEOUT,
  APP_NAME: ENV_VARS.APP_NAME,
  APP_VERSION: ENV_VARS.APP_VERSION,
  FEATURES: {
    ANALYTICS: ENV_VARS.ENABLE_ANALYTICS,
    ERROR_REPORTING: ENV_VARS.ENABLE_ERROR_REPORTING,
    DEBUG: ENV_VARS.ENABLE_DEBUG_MODE || IS_DEVELOPMENT,
    ALLOW_PAYMENT_BYPASS:
      (ENV_VARS.ENABLE_DEV_PAYMENT_BYPASS || false) && !IS_PRODUCTION,
  },
  STRIPE_PUBLIC_KEY: ENV_VARS.STRIPE_PUBLIC_KEY,
  BUNNY_CDN_URL: ENV_VARS.BUNNY_CDN_URL,
  LAZY_LOAD_DELAY: ENV_VARS.LAZY_LOAD_DELAY,
  DEBOUNCE_DELAY: ENV_VARS.DEBOUNCE_DELAY,
  CONFIG: APP_CONFIG,
};

if (IS_PRODUCTION) {
  Object.freeze(ENV);
  Object.freeze(ENV.FEATURES);
  Object.freeze(ENV.CONFIG);
}

export default ENV;
